import { getUpdatedUrl } from './urlUtil'
import {
  URL_COLUMNS,
  URL_TABLES,
  URL_VIEWS,
  URL_DATA_SOURCES_STRUCTURED,
  DATA_SOURCE_TYPE,
  ENTITY_BASED_ON_VIEWS_SUPPORTED_DATASOURCES,
  DATA_SOURCE_TYPES
} from '../constants'
import { ScanStatus } from '../interfaces'
import historyService from '../services/historyService'
import type { DropdownOption } from '../components/DropdownView/dropdownView'
import { useIntl } from 'react-intl'
import { useMemo, useCallback } from 'react'

export const isReviewNeeded = (confidence: number): boolean => confidence > 0 && confidence < 70

export const mapDataSourceGraphQlStatus = (status: string): ScanStatus => {
  if (status === 'Registering') return ScanStatus.registering
  if (status === 'Scanning') return ScanStatus.scanning
  if (status === 'SCANNING') return ScanStatus.scanning
  if (status === 'SCANNED') return ScanStatus.completed
  if (status === 'Good') return ScanStatus.completed
  if (status === 'Warning') return ScanStatus.warning
  if (status === 'Disconnected') return ScanStatus.disconnected
  if (status === 'Deleting') return ScanStatus.deleting

  return ScanStatus.registering
}

export const mapDatabaseGraphQlStatus = (status: string): ScanStatus => {
  if (status === 'registering') return ScanStatus.registering
  if (status === 'running') return ScanStatus.scanning
  if (status === 'failed') return ScanStatus.failed
  if (status === 'completed') return ScanStatus.completed

  return ScanStatus.failed
}

export enum STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME {
  PAGE_TABLES = 'tables',
  PAGE_COLUMNS = 'columns',
  PAGE_VIEWS = 'views'
}

function structuredDsTableColumnPageNameToPageUrl(pageName: STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME) {
  switch (pageName) {
    case STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME.PAGE_COLUMNS:
      return URL_COLUMNS
    case STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME.PAGE_TABLES:
      return URL_TABLES
    case STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME.PAGE_VIEWS:
      return URL_VIEWS
  }
}

export function useStructuredDsTableColumnPageSelect(datasourceId: string, datasourceType: string) {
  const intl = useIntl()

  const pageOptions: DropdownOption[] = useMemo(() => {
    const isViewsSupported = ENTITY_BASED_ON_VIEWS_SUPPORTED_DATASOURCES.includes(
      datasourceType as DATA_SOURCE_TYPES
    )

    return [
      {
        key: STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME.PAGE_TABLES,
        value: STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME.PAGE_TABLES,
        text: intl.formatMessage({ id: 'tables.viewTables' })
      },
      {
        key: STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME.PAGE_COLUMNS,
        value: STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME.PAGE_COLUMNS,
        text: intl.formatMessage({ id: 'tables.viewColumns' })
      },
      ...(isViewsSupported
        ? [
            {
              key: STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME.PAGE_VIEWS,
              value: STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME.PAGE_VIEWS,
              text: intl.formatMessage({ id: 'tables.viewByViews' })
            }
          ]
        : [])
    ]
  }, [datasourceType, intl])

  const handleChangePage = useCallback(
    (pageName: STRUCTURED_DS_TABLE_COLUMN_PAGE_NAME) => {
      const pageUrl = structuredDsTableColumnPageNameToPageUrl(pageName)

      const url = getUpdatedUrl({
        pageName: URL_COLUMNS,
        pathName: URL_DATA_SOURCES_STRUCTURED + `/${datasourceId}${pageUrl}`,
        queryParams: { [DATA_SOURCE_TYPE]: datasourceType }
      })

      return historyService.push(url)
    },
    [datasourceId, datasourceType]
  )

  return { pageOptions, handleChangePage }
}
